
import { Component } from 'vue-property-decorator';

// API
import { getAllOrganisations } from '@/utils/api/Organisations';

// Utils
import { camelCase } from 'lodash';

// Interfaces
import Organisation from '@/ship/Models/Organisation';
import { GetRequestParams } from '@/ship/Requests/RequestParams';
import OrganisationRoleEnum from '@/ship/Enums/OrganisationRoleEnum';

// Enums
import AdministrationPageNamesEnum from '@/ship/Enums/AdministrationPageNamesEnum';

// Components
import VAdministrationMain from '@/components/Administration/VAdministrationMain.vue';
import VAdministrationHeader from '@/components/Administration/VAdministrationHeader.vue';
import VPageTitle from '@/components/Administration/VPageTitle.vue';

import VAdministrationActionbar from '@/components/Administration/VAdministrationActionbar.vue';
import VAdministrationPrimaryTable from '@/components/Administration/VAdministrationPrimaryTable.vue';

// Mixins
import VAdministrationPrimaryTableMixin from '@/mixins/VAdministrationPrimaryTableMixin';
import User from '@/ship/Models/User';
import { State } from 'vuex-class';

@Component({
    components: {
        VAdministrationMain,
        VAdministrationHeader,
        VPageTitle,

        VAdministrationActionbar,
        VAdministrationPrimaryTable,
    },
})
export default class OrganisationsPage extends VAdministrationPrimaryTableMixin {
    @State user!: User;

    readonly designingRoles = [
        'designSuperAdmin',
        'generalDesigner',
        'customerdesigner',
        'generaldesignerexecutor',
        'designadmin',
        'regional_customer_design',
        'general_designer_regional',
    ];

    readonly prescriptionRoles = [
        'customer',
        'generalContractor',
        'subContractor',
        'cityAdministrator',
        'regionalLeader',
        'сhiefengineer',
    ];

    readonly currentPageName = AdministrationPageNamesEnum.Organisations;
    readonly request = getAllOrganisations;

    declare items: Organisation[];

    get transformedOrganisations() {
        return this.items.map(({ id, shortName, city, type, roles, INN, legalAddress }) => {
            const _roles = (roles || [])
                .map((item) => {
                    switch (item) {
                        case OrganisationRoleEnum.Customer:
                            return this.$t(`enums.customer`);
                        case OrganisationRoleEnum.DesignCustomer:
                            return this.$t(`enums.designCustomer`);
                        case OrganisationRoleEnum.GeneralContractor:
                            return this.$t(`enums.generalContractor`);
                        case OrganisationRoleEnum.GeneralDesigner:
                            return this.$t(`enums.generalDesigner`);
                        case OrganisationRoleEnum.Subcontractor:
                            return this.$t(`enums.subcontractor`);
                    }
                })
                .join(', ');

            return {
                id,
                shortName,
                city: city?.data.name ?? '-',
                type: this.$te(`enums.${camelCase(type)}`) ? this.$t(`enums.${camelCase(type)}`).toString() : '-',
                roles: _roles ? _roles : '-',
                INN,
                legalAddress,
            };
        });
    }

    get cityFilter(): string {
        return this.searchCityId ? `city_id:${this.searchCityId};` : '';
    }

    get searchFilter(): string {
        return this.search ? `id,city.name,short_name,INN,legal_address:${this.search};` : '';
    }

    get roleFilter(): string {
        const userRoles = this.user.roles;
        const isDesigning = userRoles?.data.some((role) => this.designingRoles.includes(role.name));
        const isPrescription = userRoles?.data.some((role) => this.prescriptionRoles.includes(role.name));

        if (isDesigning && !isPrescription)
            return `roles:${OrganisationRoleEnum.DesignCustomer},${OrganisationRoleEnum.GeneralDesigner};`;
        else if (isPrescription && !isDesigning)
            return `roles:${OrganisationRoleEnum.Customer},${OrganisationRoleEnum.Subcontractor},${OrganisationRoleEnum.GeneralContractor};`;
        return '';
    }

    public getRequestParams(): GetRequestParams {
        return {
            filter: 'id;name;short_name;city_id;type;roles;INN;legal_address;name',
            include: 'city',
            search: this.cityFilter + this.searchFilter + this.roleFilter,
        };
    }
}
